import type { Body, ContactPair, HField, Joint, Mesh } from "../../types/scene";
import { STANDARD_FLOOR_HFIELD_NAME, getStandardFloorHField } from "../../utils/terrainFloor";
import { VIEWPORT_EXPORT_CAMERA_NAME, isConveyorBeltJointName } from "./xmlGeneratorConfig";
import { reserveUniqueName } from "./xmlGenerationHelpers";
import { collectHFieldNames, collectMeshUuids, flattenBodyTree } from "./xmlGeneratorBody";
import type { ExportBodyNode, ExportCamera, ExportLight } from "./xmlGenerationTypes";

const compareExportNameSeed = (
  a: { uuid?: unknown; name?: unknown } | null | undefined,
  b: { uuid?: unknown; name?: unknown } | null | undefined
): number =>
  `${String(a?.name || "")}\0${String(a?.uuid || "")}`.localeCompare(
    `${String(b?.name || "")}\0${String(b?.uuid || "")}`
  );

export const buildCameraValuesForExport = (
  cameraMap: Record<string, ExportCamera> | undefined
): ExportCamera[] => {
  return Object.values(cameraMap || {}).filter(
    (cam) => String(cam?.name || "").trim() !== VIEWPORT_EXPORT_CAMERA_NAME
  );
};

export const buildMountedCamerasByBodyUuid = ({
  bodyTreeForExport,
  cameraValuesForExport,
}: {
  bodyTreeForExport: ExportBodyNode[];
  cameraValuesForExport: ExportCamera[];
}): Record<string, ExportCamera[]> => {
  const mountedCamerasByBodyUuid: Record<string, ExportCamera[]> = {};
  const flatBodyTree = flattenBodyTree(bodyTreeForExport);
  cameraValuesForExport.forEach((cam) => {
    if (!cam || cam.kind !== "mounted") return;
    const bodyUuid =
      cam.mountType === "site"
        ? flatBodyTree.find(
            (body) =>
              Array.isArray(body?.sites) &&
              body.sites.map(String).includes(String(cam.mountSiteUuid || ""))
          )?.uuid
        : cam.mountBodyUuid;
    if (!bodyUuid) return;
    if (!mountedCamerasByBodyUuid[bodyUuid]) mountedCamerasByBodyUuid[bodyUuid] = [];
    mountedCamerasByBodyUuid[bodyUuid].push(cam);
  });
  return mountedCamerasByBodyUuid;
};

export const buildLightValuesForExport = (
  lightMap: Record<string, ExportLight> | undefined
): ExportLight[] => Object.values(lightMap || {}).filter(Boolean);

export const buildMountedLightsByBodyUuid = ({
  bodyTreeForExport,
  lightValuesForExport,
}: {
  bodyTreeForExport: ExportBodyNode[];
  lightValuesForExport: ExportLight[];
}): Record<string, ExportLight[]> => {
  const mountedLightsByBodyUuid: Record<string, ExportLight[]> = {};
  const flatBodyTree = flattenBodyTree(bodyTreeForExport);
  lightValuesForExport.forEach((light) => {
    if (!light || light.kind !== "mounted") return;
    const bodyUuid =
      light.mountType === "site"
        ? flatBodyTree.find(
            (body) =>
              Array.isArray(body?.sites) &&
              body.sites.map(String).includes(String(light.mountSiteUuid || ""))
          )?.uuid
        : light.mountBodyUuid;
    if (!bodyUuid) return;
    if (!mountedLightsByBodyUuid[bodyUuid]) mountedLightsByBodyUuid[bodyUuid] = [];
    mountedLightsByBodyUuid[bodyUuid].push(light);
  });
  return mountedLightsByBodyUuid;
};

export const buildMaterialExportNames = (
  materials: Record<string, { uuid?: string; name?: string } | null | undefined> | undefined
): { materialUuidToName: Record<string, string>; materialNameSet: Set<string> } => {
  const materialUuidToName: Record<string, string> = {};
  const materialNameSet = new Set<string>();
  const usedMaterialNames = new Set<string>();
  Object.entries(materials || {})
    .sort(([uuidA, matA], [uuidB, matB]) =>
      compareExportNameSeed(
        { uuid: uuidA || matA?.uuid, name: matA?.name },
        { uuid: uuidB || matB?.uuid, name: matB?.name }
      )
    )
    .forEach(([uuid, mat]) => {
      if (!mat) return;
      const id = String(uuid || mat.uuid || "");
      if (!id) return;
      const name = reserveUniqueName(mat?.name, `material_${id}`, usedMaterialNames);
      materialUuidToName[id] = name;
      materialNameSet.add(name);
    });
  return { materialUuidToName, materialNameSet };
};

export const buildTextureExportNames = (
  textures: Record<string, { uuid?: string; name?: string } | null | undefined> | undefined
): Record<string, string> => {
  const textureUuidToXmlName: Record<string, string> = {};
  const usedTextureNamesForXml = new Set<string>();
  Object.entries(textures || {})
    .sort(([uuidA, textureA], [uuidB, textureB]) =>
      compareExportNameSeed(
        { uuid: uuidA || textureA?.uuid, name: textureA?.name },
        { uuid: uuidB || textureB?.uuid, name: textureB?.name }
      )
    )
    .forEach(([uuid, texture]) => {
      if (!texture) return;
      const id = String(uuid || texture.uuid || "");
      if (!id) return;
      textureUuidToXmlName[id] = reserveUniqueName(
        texture?.name,
        `texture_${id}`,
        usedTextureNamesForXml
      );
    });
  return textureUuidToXmlName;
};

export const buildSiteExportNames = ({
  siteMap,
  xmlNamingMode,
}: {
  siteMap: Record<string, { uuid?: string; name?: string } | null | undefined> | undefined;
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const siteExportNameByUuid: Record<string, string> = {};
  const usedSiteNames = new Set<string>();
  Object.values(siteMap || {})
    .sort(compareExportNameSeed)
    .forEach((site) => {
      if (!site) return;
      const uuid = site.uuid ? String(site.uuid) : "";
      if (!uuid) return;
      siteExportNameByUuid[uuid] = reserveUniqueName(
        xmlNamingMode === "uuid" ? uuid : site?.name,
        `site_${uuid}`,
        usedSiteNames
      );
    });
  return siteExportNameByUuid;
};

export const buildCameraExportNames = ({
  cameraValuesForExport,
  xmlNamingMode,
}: {
  cameraValuesForExport: ExportCamera[];
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const cameraExportNameByUuid: Record<string, string> = {};
  const usedCameraNames = new Set<string>();
  [...cameraValuesForExport].sort(compareExportNameSeed).forEach((cam) => {
    if (!cam) return;
    const uuid = cam.uuid ? String(cam.uuid) : "";
    const key = uuid || VIEWPORT_EXPORT_CAMERA_NAME;
    cameraExportNameByUuid[key] = reserveUniqueName(
      xmlNamingMode === "uuid" ? uuid : cam?.name,
      `camera_${key}`,
      usedCameraNames
    );
  });
  return cameraExportNameByUuid;
};

export const buildLightExportNames = ({
  lightValuesForExport,
  xmlNamingMode,
}: {
  lightValuesForExport: ExportLight[];
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const lightExportNameByUuid: Record<string, string> = {};
  const usedLightNames = new Set<string>();
  [...lightValuesForExport].sort(compareExportNameSeed).forEach((light) => {
    if (!light?.uuid) return;
    const uuid = String(light.uuid);
    lightExportNameByUuid[uuid] = reserveUniqueName(
      xmlNamingMode === "uuid" ? uuid : light?.name,
      `light_${uuid}`,
      usedLightNames
    );
  });
  return lightExportNameByUuid;
};

export const buildBodyExportNames = ({
  bodiesMap,
  xmlNamingMode,
}: {
  bodiesMap: Record<string, { uuid?: string; name?: string } | null | undefined> | undefined;
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const bodyUuidToXmlName: Record<string, string> = {};
  const usedBodyNames = new Set<string>();
  Object.values(bodiesMap || {})
    .sort(compareExportNameSeed)
    .forEach((body) => {
      if (!body?.uuid) return;
      const bodyUuid = String(body.uuid);
      bodyUuidToXmlName[bodyUuid] = reserveUniqueName(
        xmlNamingMode === "uuid" ? bodyUuid : body?.name,
        `body_${bodyUuid}`,
        usedBodyNames
      );
    });
  return bodyUuidToXmlName;
};

export const buildGeomExportNamesForContactPairs = ({
  bodiesMap,
  contactPairs,
  xmlNamingMode,
}: {
  bodiesMap: Record<string, Body> | undefined;
  contactPairs: ContactPair[] | undefined;
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const contactPairGeomUuids = new Set<string>();
  (contactPairs || []).forEach((pair) => {
    if (!pair) return;
    if (pair.geom1) contactPairGeomUuids.add(String(pair.geom1));
    if (pair.geom2) contactPairGeomUuids.add(String(pair.geom2));
  });
  const geomUuidToXmlName: Record<string, string> = {};
  const usedGeomNames = new Set<string>();
  Object.values(bodiesMap || {})
    .sort(compareExportNameSeed)
    .forEach((body) => {
      const geoms = Array.isArray(body?.geoms) ? body.geoms : [];
      geoms.forEach((geom) => {
        const uuid = geom?.uuid ? String(geom.uuid) : "";
        if (!uuid || !contactPairGeomUuids.has(uuid)) return;
        geomUuidToXmlName[uuid] = reserveUniqueName(
          xmlNamingMode === "uuid" ? uuid : geom?.name,
          `geom_${uuid}`,
          usedGeomNames
        );
      });
    });
  return geomUuidToXmlName;
};

export const buildJointExportNames = ({
  jointMap,
  xmlNamingMode,
}: {
  jointMap: Record<string, Joint> | undefined;
  xmlNamingMode: "readable" | "uuid";
}): Record<string, string> => {
  const jointUuidToXmlName: Record<string, string> = {};
  const usedJointNames = new Set<string>();
  Object.values(jointMap || {})
    .sort(compareExportNameSeed)
    .forEach((joint) => {
      if (!joint?.uuid) return;
      const jointUuid = String(joint.uuid);
      const sourceFreeJointName =
        joint.kind === "free" && typeof joint?.mjcfName === "string" ? joint.mjcfName.trim() : "";
      const preferredJointName =
        xmlNamingMode === "uuid" && !isConveyorBeltJointName(joint?.name)
          ? jointUuid
          : sourceFreeJointName || joint?.name;
      jointUuidToXmlName[jointUuid] = reserveUniqueName(
        preferredJointName,
        `joint_${jointUuid}`,
        usedJointNames
      );
    });
  return jointUuidToXmlName;
};

export const buildAssetUsageAndNames = ({
  bodyTreeForExport,
  meshDefinitions,
  hfieldDefinitions,
}: {
  bodyTreeForExport: ExportBodyNode[];
  meshDefinitions: Record<string, Mesh>;
  hfieldDefinitions: Record<string, HField>;
}): {
  usedMeshUuids: string[];
  usedHFieldNames: string[];
  meshUuidToXmlName: Record<string, string>;
  hfieldNameToXmlName: Record<string, string>;
  standardFloorHField: ReturnType<typeof getStandardFloorHField>;
} => {
  const usedMeshUuids = collectMeshUuids(bodyTreeForExport);
  const usedHFieldNames = collectHFieldNames(bodyTreeForExport);
  const standardFloorHField = getStandardFloorHField(hfieldDefinitions);
  if (standardFloorHField && !usedHFieldNames.includes(STANDARD_FLOOR_HFIELD_NAME)) {
    usedHFieldNames.push(STANDARD_FLOOR_HFIELD_NAME);
  }

  const meshUuidToXmlName: Record<string, string> = {};
  const emittedMeshNames = new Set<string>();
  [...usedMeshUuids]
    .sort((uuidA, uuidB) => {
      const meshA = meshDefinitions[uuidA] || null;
      const meshB = meshDefinitions[uuidB] || null;
      return `${String(meshA?.name || meshA?.url || "")}\0${uuidA}`.localeCompare(
        `${String(meshB?.name || meshB?.url || "")}\0${uuidB}`
      );
    })
    .forEach((uuid) => {
      const meshMeta = meshDefinitions[uuid] || null;
      const preferredName =
        typeof meshMeta?.name === "string" && meshMeta.name.trim()
          ? meshMeta.name.trim()
          : typeof meshMeta?.url === "string" && meshMeta.url.trim()
            ? meshMeta.url
                .trim()
                .replace(/^.*[\\/]/, "")
                .replace(/\.[^./\\]+$/, "")
            : `mesh_${uuid}`;
      meshUuidToXmlName[uuid] = reserveUniqueName(preferredName, `mesh_${uuid}`, emittedMeshNames);
    });

  const hfieldNameToXmlName: Record<string, string> = {};
  const emittedHFieldNames = new Set<string>();
  [...usedHFieldNames]
    .sort((nameA, nameB) =>
      `${String(hfieldDefinitions[nameA]?.name || nameA)}\0${nameA}`.localeCompare(
        `${String(hfieldDefinitions[nameB]?.name || nameB)}\0${nameB}`
      )
    )
    .forEach((name) => {
      const hfield = hfieldDefinitions[name] || null;
      hfieldNameToXmlName[name] = reserveUniqueName(
        hfield?.name || name,
        `hfield_${name}`,
        emittedHFieldNames
      );
    });

  return {
    usedMeshUuids,
    usedHFieldNames,
    meshUuidToXmlName,
    hfieldNameToXmlName,
    standardFloorHField,
  };
};
